import { Briefcase, Globe2, Sparkles } from "lucide-react";

const chapters = [
  { year: "2024 —", role: "Chief Growth Officer", org: "XPON Technologies (ASX:XPN)" },
  { year: "2023", role: "Chief Revenue & Customer Officer", org: "XPON Technologies" },
  { year: "2022", role: "CEO, AUNZ", org: "XPON Technologies" },
  { year: "2019", role: "Co-Founder & CEO", org: "Datisan (AI marketing analytics)" },
  { year: "2018 — 2023", role: "Recognised Expert · Advisory Board Member", org: "Advisory Board Centre" },
  { year: "Advisory", role: "GTM Advisory Boards", org: "Google Cloud · Google Marketing Platform (APAC)" },
  { year: "2015", role: "Co-Founder", org: "Winja (later ArkCX, acquired by XPON)" },
  { year: "Earlier", role: "GTM & Marketing", org: "BSR Group · Knight Frank" },
];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-xs font-mono-ui uppercase tracking-[0.28em] text-violet-400 mb-4">
          ─── The Catalyst · Founder
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-7">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-zinc-50 leading-[1.02]">
              Advisory from an
              <span className="text-zinc-500"> operator </span>
              who’s scaled it before.
            </h2>
            <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-2xl">
              Catlyz is founded and led by{" "}
              <span className="text-zinc-100">Chris Rozic</span>. Over 19+ years,
              Chris has led GTM as Chief Growth Officer, Chief Revenue Officer,
              and CEO for XPON Technologies AUNZ (ASX:XPN), and co-founded two
              SaaS businesses — Datisan (AI marketing analytics) and Winja
              (later ArkCX, acquired by XPON). He has also sat on advisory
              boards for the GTM teams at Google Cloud and Google Marketing
              Platform, guiding decisions on partner ecosystem and vertical /
              horizontal focus across APAC.
            </p>
            <p className="mt-5 text-base text-zinc-400 leading-relaxed max-w-2xl">
              Catlyz is deliberately narrow: senior GTM counsel for a small
              number of operators at a time. No agency, no implementation team,
              no billable-hour theatre — just a seasoned voice in the room when
              the decisions get expensive.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Briefcase, k: "Operator-led", v: "Chief Growth, CRO, Co-CEO — not a slide deck consultant." },
                { icon: Sparkles, k: "AI-native", v: "Built Datisan on AI/ML for marketing analytics — pre-hype." },
                { icon: Globe2, k: "Global lens", v: "GTM across AUNZ, UK & Europe. Board-level advisory." },
              ].map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.k}
                    className="border border-white/10 p-5 bg-zinc-950/60 card-hover"
                    data-testid={`about-principle-${p.k.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon className="h-4 w-4 text-violet-300" strokeWidth={1.5} />
                    <div className="mt-4 font-display text-lg text-zinc-50">
                      {p.k}
                    </div>
                    <p className="mt-1 text-xs text-zinc-400 leading-relaxed">
                      {p.v}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Career timeline panel */}
          <div className="md:col-span-5">
            <div
              data-testid="about-visual-panel"
              className="border border-white/10 bg-zinc-950/60 p-6 md:p-8"
            >
              <div className="flex items-center justify-between text-[11px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500 border-b border-white/5 pb-4">
                <span>chris_rozic.log</span>
                <span className="text-violet-400">● verified</span>
              </div>

              <div className="mt-6 flex items-center gap-4 pb-6 border-b border-white/5">
                <img
                  src="/chris-rozic.jpg"
                  alt="Chris Rozic"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextElementSibling.style.display = "flex";
                  }}
                  className="h-16 w-16 object-cover border border-white/10 shrink-0"
                  data-testid="about-profile-photo"
                />
                <div
                  className="h-16 w-16 items-center justify-center border border-white/10 bg-violet-500/10 shrink-0 font-display text-xl text-violet-300"
                  style={{ display: "none" }}
                  aria-hidden
                >
                  CR
                </div>
                <div>
                  <div className="font-display text-lg text-zinc-50 leading-tight">
                    Chris Rozic
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Founder · Catlyz
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-5">
                {chapters.map((c, i) => (
                  <div
                    key={i}
                    className="flex gap-4 items-start"
                    data-testid={`about-timeline-${i}`}
                  >
                    <div className="w-24 shrink-0 pt-0.5 font-mono-ui text-[10px] uppercase tracking-[0.18em] text-violet-300">
                      {c.year}
                    </div>
                    <div className="relative flex-1 pb-4 border-l border-white/10 pl-5">
                      <span className="absolute -left-[3px] top-2 h-1.5 w-1.5 bg-violet-400" />
                      <div className="text-sm text-zinc-100 leading-snug">
                        {c.role}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {c.org}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-white/5 text-[10px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500">
                Based Brisbane · Advises globally
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
